import React, { useCallback, useEffect, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { formChange } from '../reducers/form'
import * as actions from '../reducers/autocomplite'
import { Autocomplete, TextField, Dialog, DialogContent } from '@mui/material';
import { fetchDispath } from '../functions/fetch'
import CircularProgress from '@mui/material/CircularProgress';

const EMPTY_OBJECT = {}
const renderOption = (props, option) => (
  <li {...props} key={option.value}>
    {option.label}
  </li>
)
const isOptionEqualToValue = (option, val) => {
  if (!val) return true;
  return option?.value === val?.value;
}

/**
 * @typedef {Object} Option
 * @property {string | number} value - Уникальный идентификатор опции.
 * @property {string} label - Отображаемый текст опции.
 * @property {any} [key] - Любые дополнительные скрытые поля (например, из meta.hidden).
 */
/**
 * @type {Option[]}
 */
const EMPTY_ARRAY = [];
/**
 * @typedef {Object} AutocompleteOption
 * @property {string|number} value - Уникальный идентификатор опции.
 * @property {string} label - Отображаемый текст опции.
 * @property {any} [key] - Любые дополнительные скрытые поля (например, из meta.hidden).
 */
/**
 * @typedef {import('@mui/material').AutocompleteProps<string, false, false, false>} MUIAutocompleteProps
 * 
 * @function AutocompliteMUILoad
 * @param {Object} props - Пропсы компонента.
 * @param {string} props.field - Имя поля в стейте form
 * @param {string} props.placeHolder - Текст лейбла для поля ввода (используется как label в TextField).
 * @param {string} props.path - URL-адрес эндпоинта для запроса 
 * @param {Object} props.meta - Конфигурация обработки данных и привязки к стейту.
 * @param {string} props.meta.field - Имя поля в стейте autocomplite.
 * @param {string} props.meta.value - Ключ в объекте ответа, который будет использован как `value` опции.
 * @param {string} [props.meta.label] - Ключ в объекте ответа, который будет использован как `label` опции.
 * @param {Function} [props.meta.labelFunc] - Функция для генерации `label`, если `meta.label` не указан. Принимает исходный объект.
 * @param {string[]} [props.meta.hidden] - Массив ключей, значения которых нужно сохранить в объекте опции (скрытые данные).
 * @param {Function} [props.meta.filter] - Функция для фильтрации сырого массива данных ДО передачи в редьюсер. Принимает массив, возвращает массив.
 * @param {any} [props.meta.defaultValue] - Значение по умолчанию, которое будет выбрано в автокомплите после загрузки (должно совпадать с будущим `value`).
 * @param {boolean} [props.form] -  сохраняем в стейте store.form  default true
 * @param {string} [props.formName] -  позиция в стейте form store.form[name] 
 * @param {any} props.modal -  Модалка
 * @param {Function} [props.change] -  доп функция для изменения 
 * @param {Object|Object[]} [props.initialValue] -  начальное значение потом убрать можно
 * @param {React.ComponentProps<typeof TextField>} [props.textFieldProps]
 * @param {Omit<import('@mui/material').AutocompleteProps<AutocompleteOption, boolean, boolean, boolean>, 'options' | 'value' | 'onChange' | 'renderInput'>} [props.autocompleteProps] - 
 */
export default function AutocompliteMUILoadCreateModal({
  field,
  placeHolder,
  path,
  meta,
  change,
  form = true,
  formName,
  initialValue,
  textFieldProps = EMPTY_OBJECT,
  autocompleteProps = EMPTY_OBJECT,
  modal,
}) {
  const dispatch = useDispatch()
  const autoValue = useSelector(state => state.autocomplite[meta.field]?.value || null)
  const autoArr = useSelector(state => state.autocomplite[meta.field]?.arr) || EMPTY_ARRAY
  const autoLoad = useSelector(state => state.autocomplite[meta.field]?.load || false)
  const [openDialog, setOpenDialog] = useState(false);
  useEffect(() => {
    dispatch(actions.initialAutocomplite({ field: meta.field, value: initialValue }))
    return () => {
      dispatch(actions.clearAutocomplite({ field: meta.field }))
    }
  }, [dispatch, meta.field, initialValue])
  useEffect(() => {
    if (autoLoad) return;
    dispatch(actions.loadAutocompliteAPI(path, meta))
  }, [dispatch, path, meta, autoLoad])

  // костыль 
  const valueNUll = autoValue?.value === '' ? null : autoValue
  const handleChange = useCallback((event, newValue) => {
    const value = (newValue) ? newValue.value : null
    dispatch(actions.set(newValue, { field: meta.field }))
    if (form) dispatch(formChange(value, { field: field, name: formName }))
    if (change) change(newValue)
  }, [dispatch, change, formName, field, form, meta.field])
  const handleCreateChange = async (event, newValue) => {
    // Если выбран вариант "Создать новый"
    if (newValue?.isNew) {
      setOpenDialog(true);
    } else {
      // Обычное изменение значения - вызываем ваш существующий handleChange
      handleChange(event, newValue);
    }
  };
  const renderInput = useCallback((params) => {
    return (
      <TextField
        {...params}
        variant="standard"
        label={textFieldProps?.label !== undefined ? textFieldProps.label : placeHolder}
        InputLabelProps={{
          shrink: true,
          ...textFieldProps.InputLabelProps,
        }}
        {...textFieldProps}
      />
    );
  }, [placeHolder, textFieldProps])
  const handleAdd = (res) => {
    setOpenDialog(false)
    if (res.success) {
      let row = { value: res.get[meta.value] + '', label: '' }
      if (meta.hidden) meta.hidden.forEach(key => row[key] = res.get[key])
      if (meta.label) row.label = res.get[meta.label] + ''
      else row.label = meta?.labelFunc(res.get) + ''
      dispatch(actions.add(row, { field: meta.field }))
      handleChange(null, row)
    }
  }
  const {
    options: _ignoredOptions,
    value: _ignoredValue,
    onChange: _ignoredOnChange,
    renderInput: _ignoredRenderInput,
    renderOption: _renderOption,
    filterOptions: _filterOptions,
    ...safeAutocompleteProps
  } = autocompleteProps;
  return (
    <>
      <Autocomplete
        options={autoArr}
        value={valueNUll}
        onChange={handleCreateChange}
        isOptionEqualToValue={isOptionEqualToValue}
        renderOption={renderOption}
        renderInput={renderInput}
        filterOptions={(options, state) => {
          const filtered = options.filter((option) =>
            option.label.toLowerCase().includes(state.inputValue.toLowerCase())
          );
          if (state.inputValue !== '') {
            const exists = options.some(
              opt => opt.label.toLowerCase() === state.inputValue.toLowerCase()
            );
            if (!exists) {
              filtered.push({
                value: 'create-new',
                label: `+ Создать "${state.inputValue}"`,
                inputValue: state.inputValue,
                isNew: true,
              });
            }
          }

          return filtered;
        }}
        {...safeAutocompleteProps}
      />
      <Dialog
        open={openDialog}
        maxWidth='sm'
        keepMounted
        fullWidth
        scroll="paper"
        onClose={() => setOpenDialog(false)}
        PaperProps={{ style: { height: 400 } }}
      >
        <DialogContent >
          {modal({ handleClose: handleAdd })}
        </DialogContent>
      </Dialog>
    </>
  );
}